import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";

import { useWorkspace } from "src/contexts/global/WorkspaceContext";
import type { AttributePolicyByKey } from "src/pages/OrgDesignSettings/attributeDefaultsTypes";
import { parsePersistedAttributePolicies } from "src/pages/OrgDesignSettings/attributePolicy";
import {
  parsePersistedAttributeLabels,
  type AttributeLabelsByKey,
} from "src/pages/OrgDesignSettings/attributeLabels";
import { getOrgDesignWorkspaceConfig } from "src/queries/orgDesignWorkspaceConfig.graphql";
import {
  resolveDefaultInsightsHierarchyView,
  type InsightsHierarchyView,
} from "src/util/insightsHierarchyView";
import { isRecord } from "src/util/typeGuards";

import {
  resolveInsightsHierarchy,
  type InsightsHierarchyConfig,
} from "./insightsHierarchyOptions";
import {
  resolveInsightsTargets,
  type InsightsTargetsConfig,
} from "./insightsTargets";

export type OrgDesignConfig = {
  ready: boolean;
  loading: boolean;
  hierarchy: InsightsHierarchyConfig;
  targets: InsightsTargetsConfig;
  showTargets: boolean;
  allowLensSwitching: boolean;
  defaultView: InsightsHierarchyView;
  defaultViewRaw: unknown;
  insightsHierarchyRaw: unknown;
  attributePolicies: AttributePolicyByKey;
  attributeLabels: AttributeLabelsByKey;
};

/**
 * Dedicated getOrgDesignWorkspaceConfig result, only when it is the active
 * workspace. Stale source-workspace data after a clone switch must not win
 * over FullWorkspaceFragment config.
 */
const matchingOrgDesignQueryConfig = (
  data: unknown,
  workspaceId: string | undefined
): Record<string, unknown> | undefined => {
  if (!workspaceId || !isRecord(data) || !isRecord(data["workspace"])) {
    return undefined;
  }
  const queried = data["workspace"];
  if (queried["id"] !== workspaceId || !isRecord(queried["config"])) {
    return undefined;
  }
  return queried["config"];
};

/**
 * Org Design workspace config (insightsHierarchy + default lens + attribute policies).
 */
export const useOrgDesignConfig = (): OrgDesignConfig => {
  const workspace = useWorkspace();
  const workspaceId =
    typeof workspace?.id === "string" ? workspace.id : undefined;
  const workspaceConfig = isRecord(workspace?.config)
    ? workspace.config
    : undefined;
  const { data, loading } = useQuery(getOrgDesignWorkspaceConfig, {
    // Ignore field errors so an older API cannot blank cloned OD settings
    // already present on FullWorkspaceFragment / WorkspaceContext.
    errorPolicy: "ignore",
    fetchPolicy: "cache-and-network",
    variables: { workspaceId },
    skip: !workspaceId,
  });

  const queryConfig = matchingOrgDesignQueryConfig(data, workspaceId);
  const insightsHierarchyRaw =
    queryConfig?.["insightsHierarchy"] ??
    workspaceConfig?.["insightsHierarchy"];
  const defaultViewRaw =
    queryConfig?.["defaultInsightsHierarchyView"] ??
    workspaceConfig?.["defaultInsightsHierarchyView"];
  const attributePoliciesRaw =
    queryConfig?.["orgChartAttributePolicies"] ??
    workspaceConfig?.["orgChartAttributePolicies"];
  const attributeLabelsRaw =
    queryConfig?.["orgChartAttributeLabels"] ??
    workspaceConfig?.["orgChartAttributeLabels"];

  const settled =
    Boolean(workspaceId) &&
    (!loading ||
      queryConfig !== undefined ||
      insightsHierarchyRaw !== undefined);

  const hierarchy = useMemo(
    () => resolveInsightsHierarchy(insightsHierarchyRaw),
    [insightsHierarchyRaw]
  );
  const targets = useMemo(
    () =>
      resolveInsightsTargets(
        isRecord(insightsHierarchyRaw)
          ? insightsHierarchyRaw["targets"]
          : undefined
      ),
    [insightsHierarchyRaw]
  );
  const defaultView = useMemo(
    () =>
      resolveDefaultInsightsHierarchyView(
        typeof defaultViewRaw === "string" ? defaultViewRaw : undefined
      ),
    [defaultViewRaw]
  );
  const attributePolicies = useMemo(
    () => parsePersistedAttributePolicies(attributePoliciesRaw),
    [attributePoliciesRaw]
  );
  const attributeLabels = useMemo(
    () => parsePersistedAttributeLabels(attributeLabelsRaw),
    [attributeLabelsRaw]
  );

  return {
    ready: settled,
    loading,
    hierarchy,
    targets,
    showTargets: settled && hierarchy.showTargets,
    allowLensSwitching: hierarchy.allowLensSwitching,
    defaultView,
    defaultViewRaw,
    insightsHierarchyRaw,
    attributePolicies,
    attributeLabels,
  };
};
